/**
 * Agent Status Store Slice
 * 
 * Bu slice, agent'ların durum yönetimi için state ve action'ları sağlar.
 */

import { toast } from 'react-hot-toast';
import { api } from '../../services/authService';
import logger from '../../utils/logger';
import { formatMinutesLong } from '../../utils/dateUtils';
import { mediaErrorMessage } from '../../utils/mediaReadiness';

// Müsait'e geçiş kapısı: buton, mola zamanlayıcısı ve kayıt aynı anda tetikleyebilir — tek deneme.
let setAvailableInFlight = null;

const createAgentStatusSlice = (set, get) => ({
    // State
    currentStatus: {
        status_type: 'busy',
        status_message: null,
        estimated_duration: null,
        start_time: new Date(),
        is_active: true
    },
    statusHistory: [],
    statusTypes: [
        { value: 'available', label: 'Müsait', color: 'green', icon: '🟢' },
        { value: 'busy', label: 'Meşgul', color: 'red', icon: '🔴' },
        { value: 'break', label: 'Mola', color: 'yellow', icon: '🟡' },
        { value: 'lunch', label: 'Yemek', color: 'orange', icon: '🟠' },
        { value: 'training', label: 'Eğitim', color: 'blue', icon: '🔵' },
        { value: 'technical_issue', label: 'Teknik Arıza', color: 'purple', icon: '🟣' },
        { value: 'offline', label: 'Çevrimdışı', color: 'gray', icon: '⚫' }
    ],
    durationOptions: [
        { value: 5, label: '5 dakika' },
        { value: 10, label: '10 dakika' },
        { value: 15, label: '15 dakika' },
        { value: 30, label: '30 dakika' },
        { value: 60, label: '1 saat' },
        { value: 120, label: '2 saat' },
        { value: 0, label: 'Özel süre' }
    ],
    isLoading: false,
    error: null,

    // Pagination state'leri ekle
    statusHistoryPagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false,
        nextPage: null,
        prevPage: null
    },
    statusHistoryFilters: {
        start_date: '',
        end_date: '',
        status_type: ''
    },
    statusHistoryStats: [],

    // Actions
    setCurrentStatus: (status) => {
        set({ currentStatus: status });
    },

    setStatusHistory: (history) => {
        set({ statusHistory: history });
    },

    setLoading: (loading) => {
        set({ isLoading: loading });
    },

    setError: (error) => {
        set({ error });
    },

    // Pagination actions
    setStatusHistoryPagination: (pagination) => {
        set({ statusHistoryPagination: pagination });
    },

    setStatusHistoryFilters: (filters) => {
        set({ statusHistoryFilters: filters });
    },

    setStatusHistoryStats: (stats) => {
        set({ statusHistoryStats: stats });
    },

    // API Actions
    getCurrentStatus: async (agentId) => {
        const { setCurrentStatus, setLoading, setError } = get();

        try {
            setLoading(true);
            setError(null);

            const response = await api.get(`agent-status/current/${agentId}`);

            if (response.data.success) {
                setCurrentStatus(response.data.data);
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Statü getirilemedi');
            }

        } catch (error) {
            logger.error('Mevcut statü getirme hatası:', error);
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    },

    // options.force: kamera/mikrofon kapısının yazımı, paylaşılan isLoading (geçmiş/mevcut statü yüklemesi)
    // yüzünden sessizce düşmesin.
    updateStatus: async (agentId, statusData, options = {}) => {
        const { setCurrentStatus, setLoading, setError, changeStatusViaSocket } = get();

        // Eğer zaten loading durumundaysa, duplicate çağrıyı engelle
        if (get().isLoading && !options.force) {
            return null;
        }

        try {
            setLoading(true);
            setError(null);

            // Önce socket üzerinden statü değişikliği gönder
            changeStatusViaSocket(statusData);

            // API'ye de gönder (veritabanı için)
            const response = await api.post(`agent-status/update/${agentId}`, statusData);

            if (response.data.success) {
                setCurrentStatus(response.data.data);
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Statü güncellenemedi');
            }

        } catch (error) {
            logger.error('Statü güncelleme hatası:', error);
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    },

    // Socket Actions
    changeStatusViaSocket: (statusData) => {
        const { socket } = get();

        if (!socket || !socket.connected) {
            return;
        }

        socket.emit('agent-status-change', statusData);
    },

    // Agent list güncelleme fonksiyonu ekleyelim
    updateAgentList: (agents) => {
        set({ agents });
    },

    // Agent statü güncelleme event'lerini dinle
    setupAgentStatusListeners: (socket) => {
        if (!socket) return;

        // Agent listesi güncellemesi
        socket.on('agent-list-update', (agents) => {

            get().updateAgentList(agents);
        });

        // Agent statü güncelleme bildirimi
        socket.on('agent-status-updated', (data) => {

            if (data.success) {
                const { setCurrentStatus } = get();
                setCurrentStatus(data.data);
            } else {
            }
        });
    },

    // Utility Actions
    getStatusTypeInfo: (statusType) => {
        const { statusTypes } = get();
        return statusTypes.find(type => type.value === statusType) || statusTypes[0];
    },

    getDurationOption: (duration) => {
        const { durationOptions } = get();
        return durationOptions.find(option => option.value === duration) || durationOptions[0];
    },

    calculateEndTime: (duration) => {
        if (!duration || duration === 0) return null;
        const endTime = new Date();
        endTime.setMinutes(endTime.getMinutes() + duration);
        return endTime;
    },

    formatDuration: formatMinutesLong,

    // Quick Status Actions
    // Müsait olmak kamera+mikrofon kontrolünü geçmeyi gerektirir (videocall ortak kaydı §B.5); geçmezse
    // temsilci 'technical_issue' olur ve sunucu ona müşteri atamaz.
    setAvailable: (agentId) => {
        if (setAvailableInFlight) return setAvailableInFlight;
        setAvailableInFlight = (async () => {
            const wasAvailable = get().currentStatus?.status_type === 'available';
            const { ready, errorCode } = await get().checkMediaReadiness();
            if (!ready) {
                // Zaten müsaitse applyMediaFailurePolicy technical_issue'ya aldı; ikinci yazım/uyarı yok.
                if (wasAvailable) return null;
                toast.error(mediaErrorMessage(errorCode));
                return await get().setTechnicalIssue(agentId, 'Kamera/mikrofon hazır değil', { force: true });
            }
            return await get().updateStatus(agentId, {
                status_type: 'available',
                status_message: null,
                estimated_duration: null
            });
        })().finally(() => { setAvailableInFlight = null; });
        return setAvailableInFlight;
    },

    setBreak: async (agentId, duration = 15, message = 'Mola') => {
        return await get().updateStatus(agentId, {
            status_type: 'break',
            status_message: message,
            estimated_duration: duration,
            end_time: get().calculateEndTime(duration)
        });
    },

    setLunch: async (agentId, duration = 60, message = 'Yemek molası') => {
        return await get().updateStatus(agentId, {
            status_type: 'lunch',
            status_message: message,
            estimated_duration: duration,
            end_time: get().calculateEndTime(duration)
        });
    },

    setTraining: async (agentId, duration = 120, message = 'Eğitim') => {
        return await get().updateStatus(agentId, {
            status_type: 'training',
            status_message: message,
            estimated_duration: duration,
            end_time: get().calculateEndTime(duration)
        });
    },

    setTechnicalIssue: async (agentId, message = 'Teknik arıza', options = {}) => {
        return await get().updateStatus(agentId, {
            status_type: 'technical_issue',
            status_message: message,
            estimated_duration: null
        }, options);
    },

    // Eksik fonksiyonları ekleyelim
    setBusy: async (agentId, message = 'Meşgul') => {
        return await get().updateStatus(agentId, {
            status_type: 'busy',
            status_message: message,
            estimated_duration: null
        });
    },

    setOffline: async (agentId, message = 'Çevrimdışı') => {
        return await get().updateStatus(agentId, {
            status_type: 'offline',
            status_message: message,
            estimated_duration: null
        });
    },



    // Güncellenmiş getStatusHistory fonksiyonu
    getStatusHistory: async (agentId, page = 1, filters = {}) => {
        const { setStatusHistory, setStatusHistoryPagination, setStatusHistoryFilters, setStatusHistoryStats, setLoading, setError } = get();

        try {
            setLoading(true);
            setError(null);

            // Query parametrelerini oluştur
            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: '10',
                ...filters
            });

            const response = await api.get(`agent-status/history/${agentId}?${queryParams}`);

            if (response.data.success) {
                const { statuses, pagination, stats, filters: responseFilters } = response.data.data;

                setStatusHistory(statuses);
                setStatusHistoryPagination(pagination);
                setStatusHistoryStats(stats);
                setStatusHistoryFilters(responseFilters);

                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Statü geçmişi getirilemedi');
            }

        } catch (error) {
            logger.error('Statü geçmişi getirme hatası:', error);
            setError(error.message);
            return null;
        } finally {
            setLoading(false);
        }
    },

    // Pagination navigation fonksiyonları
    goToPage: async (agentId, page) => {
        const { statusHistoryFilters } = get();
        return await get().getStatusHistory(agentId, page, statusHistoryFilters);
    },

    goToNextPage: async (agentId) => {
        const { statusHistoryPagination } = get();
        if (statusHistoryPagination.hasNextPage) {
            return await get().goToPage(agentId, statusHistoryPagination.nextPage);
        }
        return null;
    },

    goToPrevPage: async (agentId) => {
        const { statusHistoryPagination } = get();
        if (statusHistoryPagination.hasPrevPage) {
            return await get().goToPage(agentId, statusHistoryPagination.prevPage);
        }
        return null;
    },

    // Filtreleme fonksiyonu
    applyStatusHistoryFilters: async (agentId, filters) => {
        return await get().getStatusHistory(agentId, 1, filters); // İlk sayfaya dön
    },
});


export default createAgentStatusSlice;
